// TODO: remove this after company rebuild task done
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, forwardRef, useRef } from 'react'
import type { Ref } from 'react'
import clsx from 'clsx'
import { isEmpty, noop } from 'lodash'

import { Checkbox, Chip } from 'components'
import { useOnClickOutside } from 'helpers/hooks'
import { getLabel } from 'helpers/language'
import { IconArrowDown } from '../Icons'

import type { OptionProps } from 'types'

export interface DropdownProps {
  isTime?: boolean
  placeholder?: string
  selected?: OptionProps | null
  onChange?: (selected: OptionProps) => void
  onBlur?: () => void
  options: OptionProps[]
  label?: string
  disabled?: boolean
  className?: string
  variants?: 'primary' | 'secondary'
  withIcon?: React.ReactNode
  group?: 'left' | 'right'
  isError?: boolean
  Search?: React.ReactNode
  fullWidth?: boolean
  placement?: 'top'
  keyTest?: string
  isShowIcon?: boolean
  isCheckbox?: boolean
  countCheckbox?: number
  displaySelect?: boolean
  selectCheckbox?: (key: string) => void
  onFilterCheckBox?: () => void
  onCloseChip?: (value: boolean) => void
}

function Dropdown(props: DropdownProps, ref: Ref<HTMLDivElement>): JSX.Element {
  const {
    isTime = false,
    placeholder = '',
    selected,
    label,
    disabled = false,
    className,
    onChange,
    onBlur,
    variants,
    options,
    group,
    fullWidth = false,
    isError = false,
    withIcon,
    placement,
    keyTest = 'test-dropdown',
    isShowIcon,
    isCheckbox,
    countCheckbox = 0,
    displaySelect = false,
    selectCheckbox = () => {},
    onFilterCheckBox = () => {},
    onCloseChip = () => {},
  } = props
  const [show, setShow] = useState<boolean>(false)

  const selectedOption = typeof selected === 'object' && !isEmpty(selected)
  const isOneOption = options.length === 1

  const divRef = useRef<HTMLDivElement>(null)
  const isCheckBoxFilter = Boolean(
    selected && isCheckbox && show && displaySelect,
  )
  const isUnSelectedAll =
    selected?.label === 'common:all.label' && !Boolean(selected?.isSelect)

  const _handleClose = () => {
    if (isCheckBoxFilter || isUnSelectedAll) {
      onFilterCheckBox()
    }

    setShow(false)
  }

  const _handleToggle = () => {
    setShow((prevShow) => !prevShow)
  }

  useOnClickOutside(divRef, () => {
    _handleClose()
  })

  const handleSelected = (_selected: OptionProps) => {
    if (!onChange) {
      return null
    }

    onChange(_selected)
    if (!isCheckbox) {
      _handleClose()
    }
  }

  const displayChip = (isDisplay: boolean, count: number) => {
    const isCountCheckbox = Boolean(count > 0)
    const dataDisplayChip = options.filter((e) => e.isSelect)

    const key = !isEmpty(dataDisplayChip) ? dataDisplayChip[0].key : ''
    const label = !isEmpty(dataDisplayChip) ? dataDisplayChip[0].label : ''

    return (
      isDisplay && (
        <div className='dropdown__checkbox'>
          <Chip
            className='chip__search__custom'
            onClose={() => {
              selectCheckbox(key)
              onCloseChip(true)
            }}
            isShortText={isCountCheckbox}
            label={label}
            data-testid={`chip-${key}`}
          />
          {isCountCheckbox && <label>{` + ${count}`}</label>}
        </div>
      )
    )
  }

  const renderArrowIcon = (isOneOption: boolean) => {
    if (isOneOption) {
      return null
    }

    return (
      <IconArrowDown
        className={clsx(show && 'dropdown__rotate')}
        variants='sm'
      />
    )
  }

  let styleLabel

  if (isCheckbox && !displaySelect) {
    styleLabel = `label-checkbox`
  }

  const getOptionItems = (
    isTime: boolean,
    isCheckbox: boolean,
    option: OptionProps<any>,
    index: number,
    selected: OptionProps<any> | null | undefined,
    selectCheckbox: (key: string) => void,
    handleSelected: (_selected: OptionProps) => null | undefined,
  ) => {
    let isSelected = selected?.key === option.key

    if (Boolean(selected?.key.id)) {
      isSelected = selected?.key.id === option.key.id
    }

    if (isCheckbox) {
      return (
        <Checkbox
          key={option.key}
          name={option.label}
          checked={option.isSelect || false}
          onChange={() => {
            selectCheckbox(option.key)
          }}
          label={option.label}
          isDropdown
        />
      )
    }

    return (
      <div
        key={index}
        className={clsx(
          'dropdown__option',
          isSelected && 'dropdown__option--selected',
        )}
        tabIndex={-1}
        onClick={() => handleSelected(option)}
        data-testid={`${keyTest}-${index}`}
      >
        {isShowIcon ? option.icon : getLabel(option.label, isTime)}
      </div>
    )
  }

  const renderOptionList = (
    isTime: boolean,
    isCheckbox: boolean,
    options: OptionProps<any>[],
    selected: OptionProps<any> | null | undefined,
    selectCheckbox: (key: string) => void,
    handleSelected: (_selected: OptionProps) => null | undefined,
  ) => {
    return options.map((option, index) =>
      getOptionItems(
        Boolean(isTime),
        Boolean(isCheckbox),
        option,
        index,
        selected,
        selectCheckbox,
        handleSelected,
      ),
    )
  }

  const renderSelectedText = (
    isTime: boolean,
    isCheckbox: boolean,
    isShow: boolean,
    label?: string,
  ) => {
    const selectedText = getLabel(label, isTime)

    return isCheckbox
      ? isShow && <React.Fragment>{selectedText}</React.Fragment>
      : selectedText
  }

  const renderSelectedOption = (
    isTime: boolean,
    isSelectedOptionWithTitle: boolean,
    isShowSelectedLabel: boolean,
    isSelectedOption: boolean,
    isShowIcon: boolean,
    isCheckbox: boolean,
    className?: string,
    title?: string,
    label?: string,
    icon?: React.ReactElement,
  ) => {
    const otherSelectedLabel = isShowIcon
      ? icon
      : isSelectedOption && getLabel(label, isTime)

    return isSelectedOptionWithTitle ? (
      <React.Fragment>
        <span data-testid='dropdown-label' className={className || ''}>
          {getLabel(title)}
        </span>
        {renderSelectedText(
          Boolean(isTime),
          Boolean(isCheckbox),
          Boolean(isShowSelectedLabel),
          label,
        )}
      </React.Fragment>
    ) : (
      otherSelectedLabel
    )
  }

  return (
    <div
      className={clsx(
        'dropdown',
        {
          'dropdown--disabled': disabled,
          [`dropdown--${variants}`]: Boolean(variants),
          [`dropdown--error`]: isError,
          [`dropdown--full-width`]: fullWidth,
        },
        className,
      )}
      ref={ref}
      data-testid='dropdown-test'
      onBlur={onBlur}
    >
      <div className='dropdown__container' ref={divRef}>
        <div
          className={clsx(
            'dropdown__control',
            group && `dropdown__control--${group}-group`,
          )}
          onClick={disabled || isOneOption ? noop : _handleToggle}
          data-testid='dropdown-container'
        >
          <div
            className={clsx(
              'dropdown__single-value',
              !selectedOption && 'dropdown__placeholder',
            )}
            data-testid='dropdown-placeholder'
          >
            {renderSelectedOption(
              Boolean(isTime),
              Boolean(selectedOption && label),
              Boolean(show && displaySelect),
              Boolean(selectedOption),
              Boolean(isShowIcon),
              Boolean(isCheckbox),
              styleLabel,
              label,
              selected?.label,
              selected?.icon,
            )}

            {!selectedOption && placeholder}

            {displayChip(
              Boolean(selected && isCheckbox && !show && displaySelect),
              countCheckbox,
            )}
          </div>
          <div className='dropdown__indicator'>
            {!isEmpty(withIcon) ? withIcon : renderArrowIcon(isOneOption)}
          </div>
        </div>
        <div
          className={clsx(
            'dropdown__menu',
            show && 'dropdown__menu--show',
            placement && `dropdown__menu--${placement}`,
          )}
          data-testid='dropdown-menu'
        >
          <div
            id='dropdown-menu-list'
            className={`dropdown__menu-list${
              isCheckbox ? ' dropdown--ternary' : ''
            }`}
          >
            {renderOptionList(
              Boolean(isTime),
              Boolean(isCheckbox),
              options,
              selected,
              selectCheckbox,
              handleSelected,
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default forwardRef(Dropdown)
